import dayjs from 'dayjs';
import { BinState } from './binSetConstants';
import { DATE_FORMAT_MONTH_DAY_YEAR } from './dates';

/**
 * When only bin-set total mass is known, assign each bin a mass proportional to its capacity
 * so every bin shows the same fill ratio (total / sum of capacities) in bin visuals.
 *
 * @param {Array<{ capacityInGrams?: number }>} bins
 * @param {number} binSetLevelGrams
 * @returns {number[] | null} Grams per bin (same order as `bins`), or null if not computable.
 */
export function splitBinSetLevelGramsByBinCapacities(bins, binSetLevelGrams) {
  if (!Number.isSafeInteger(binSetLevelGrams)) return null;
  const caps = bins.map((b) => Math.max(0, b.capacityInGrams || 0));
  const totalCap = caps.reduce((a, c) => a + c, 0);
  if (totalCap <= 0) return null;
  return caps.map((c) => (c > 0 ? (binSetLevelGrams * c) / totalCap : 0));
}

export function calcBinState(
  binSetLevelInGrams,
  earliest_delivery_bin_set_level_in_grams,
  runout_prevention_bin_set_level_in_grams,
) {
  if (!binSetLevelInGrams || !runout_prevention_bin_set_level_in_grams) return BinState.UNKNOWN;
  if (binSetLevelInGrams < runout_prevention_bin_set_level_in_grams) return BinState.ERROR;
  if (!earliest_delivery_bin_set_level_in_grams) return BinState.UNKNOWN;
  if (earliest_delivery_bin_set_level_in_grams >= binSetLevelInGrams) return BinState.WARNING;
  return BinState.SUCCESS;
}

function hasValidBinSetLevelInGrams(binSetLevelInGrams) {
  return typeof binSetLevelInGrams === 'number' && Number.isSafeInteger(binSetLevelInGrams);
}

export function calcEmptyIn(emptyAt, binSetLevelInGrams) {
  if (emptyAt == null || emptyAt === 0) {
    if (hasValidBinSetLevelInGrams(binSetLevelInGrams)) {
      return {
        emptyInHeading: 'Empty In',
        emptyInString: '> 4 weeks',
      };
    }
    return {
      emptyInHeading: 'Empty In',
      emptyInString: 'Unknown',
    };
  }
  const emptyAtDayjs = dayjs.tz(1000 * emptyAt);
  if (emptyAtDayjs <= dayjs.tz()) {
    // Empty at date is in the past.
    return { emptyInHeading: 'Empty Since', emptyInString: emptyAtDayjs.format(DATE_FORMAT_MONTH_DAY_YEAR) };
  } else {
    const emptyInDuration = dayjs.duration(dayjs.tz(emptyAt * 1000).diff(dayjs.tz()));
    const emptyInDays = Math.floor(emptyInDuration.asDays());
    const emptyInHours = Math.floor(emptyInDuration.asHours());
    return {
      emptyInHeading: 'Empty In',
      emptyInString: emptyInDays >= 2 ? `${emptyInDays} days` : `${emptyInHours} hour${emptyInHours !== 1 ? 's' : ''}`,
    };
  }
}
